import {
  Query, QueryConstraint, DocumentReference, DocumentSnapshot, QuerySnapshot,
  addDoc, collection, deleteDoc, doc, getDoc, getDocs, limit, onSnapshot,
  orderBy, query, setDoc, updateDoc, where, writeBatch
} from 'firebase/firestore';
import { getDownloadURL, ref as storageRef, uploadBytesResumable, deleteObject } from 'firebase/storage';
import { onValue, ref as databaseRef, update as updateDatabase } from 'firebase/database';
import { auth } from './client';
import { database, db, functions, storage } from './services';
import { onAuthStateChanged } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';

const wrapDocSnapshot = (snapshot: DocumentSnapshot) => ({
  id: snapshot.id,
  exists: snapshot.exists(),
  data: () => snapshot.data(),
  ref: new LegacyDocumentReference(snapshot.ref)
});

const wrapQuerySnapshot = (snapshot: QuerySnapshot) => ({
  docs: snapshot.docs.map(wrapDocSnapshot),
  size: snapshot.size,
  empty: snapshot.empty,
  forEach: (callback: (docSnapshot: ReturnType<typeof wrapDocSnapshot>, index?: number) => void) =>
    snapshot.docs.forEach((item, index) => callback(wrapDocSnapshot(item), index)),
  docChanges: () => snapshot.docChanges().map(change => ({
    type: change.type,
    oldIndex: change.oldIndex,
    newIndex: change.newIndex,
    doc: wrapDocSnapshot(change.doc)
  }))
});

class LegacyDocumentReference {
  constructor(readonly raw: DocumentReference) {}
  get id() { return this.raw.id; }
  get path() { return this.raw.path; }
  collection(path: string) { return new LegacyQueryReference(collection(this.raw, path), `${this.raw.path}/${path}`); }
  get() { return getDoc(this.raw).then(wrapDocSnapshot); }
  set(data: unknown, options?: { merge?: boolean }) {
    return options ? setDoc(this.raw, data as never, options) : setDoc(this.raw, data as never);
  }
  update(data: unknown) { return updateDoc(this.raw, data as never); }
  delete() { return deleteDoc(this.raw); }
  onSnapshot(callback: (snapshot: ReturnType<typeof wrapDocSnapshot>) => void, error?: (error: Error) => void) {
    return onSnapshot(this.raw, snapshot => callback(wrapDocSnapshot(snapshot)), error);
  }
}

class LegacyQueryReference {
  constructor(readonly raw: Query, private readonly collectionPath: string) {}
  doc(id?: string) { return new LegacyDocumentReference(id ? doc(db, this.collectionPath, id) : doc(collection(db, this.collectionPath))); }
  where(field: string, operator: Parameters<typeof where>[1], value: unknown) {
    return new LegacyQueryReference(query(this.raw, where(field, operator, value)), this.collectionPath);
  }
  orderBy(field: string, direction?: 'asc' | 'desc') {
    return new LegacyQueryReference(query(this.raw, orderBy(field, direction)), this.collectionPath);
  }
  limit(count: number) { return new LegacyQueryReference(query(this.raw, limit(count)), this.collectionPath); }
  get() { return getDocs(this.raw).then(wrapQuerySnapshot); }
  add(data: unknown) { return addDoc(this.raw as never, data as never).then(ref => new LegacyDocumentReference(ref)); }
  onSnapshot(callback: (snapshot: ReturnType<typeof wrapQuerySnapshot>) => void, error?: (error: Error) => void) {
    return onSnapshot(this.raw, snapshot => callback(wrapQuerySnapshot(snapshot)), error);
  }
}

const legacyFirestore = {
  collection: (path: string) => new LegacyQueryReference(collection(db, path), path),
  batch: () => {
    const batch = writeBatch(db);
    return {
      set: (ref: LegacyDocumentReference, data: unknown, options?: { merge?: boolean }) => {
        options ? batch.set(ref.raw, data as never, options) : batch.set(ref.raw, data as never);
      },
      update: (ref: LegacyDocumentReference, data: unknown) => batch.update(ref.raw, data as never),
      delete: (ref: LegacyDocumentReference) => batch.delete(ref.raw),
      commit: () => batch.commit()
    };
  }
};

const legacyStorageRoot = {
  child: (path: string) => {
    const reference = storageRef(storage, path);
    return {
      put: (file: Blob | Uint8Array | ArrayBuffer) => {
        const task = uploadBytesResumable(reference, file);
        return {
          on: (_event: string, next?: (snapshot: unknown) => void, error?: (error: Error) => void, complete?: () => void) =>
            task.on('state_changed', next as never, error, complete),
          get snapshot() {
            return { ...task.snapshot, ref: { ...task.snapshot.ref, getDownloadURL: () => getDownloadURL(task.snapshot.ref) } };
          }
        };
      },
      delete: () => deleteObject(reference),
      getDownloadURL: () => getDownloadURL(reference),
      raw: reference
    };
  }
};

const legacyDatabase = {
  ref: (path: string) => {
    const reference = databaseRef(database, path);
    return {
      on: (_event: 'value', callback: (snapshot: unknown) => void) => onValue(reference, callback as never),
      update: (values: Record<string, unknown>) => updateDatabase(reference, values)
    };
  }
};

export const legacyAdapter = {
  firestore: legacyFirestore,
  storageRoot: legacyStorageRoot,
  database: legacyDatabase,
  functions: { httpsCallable: (name: string) => httpsCallable(functions, name) },
  auth: {
    get currentUser() { return auth.currentUser; },
    onAuthStateChanged: (callback: Parameters<typeof onAuthStateChanged>[1]) => onAuthStateChanged(auth, callback)
  }
};
